/**
 * interior_kit_props.ts — THE PROCEDURAL KIT PIECES (the interiors programme, slice F, 2026-10-01). PURE descriptions:
 * no three, no DOM, no imports. The bed (`src/probe/interior_plan_bed.ts`) and the visit (slice C) build the meshes.
 *
 * A PROP is a thing a room wants that is honestly a few boxes a material carries — a folding screen, a low riser, a
 * queue rail, a counter front, a door plate. It costs no generation credit and exists TODAY; where a generated model
 * would read better (`wants`), the prop is its stand-in (docs/research/2026-10-01-interiors/wanted-items.md).
 *
 * TWO KINDS
 *   · `piece`   — a PLANNER piece like any catalog piece: drawn bounds, `blocksWalk`, a cover height, kit anchors.
 *                 `interior_kits.ts pieceMeta(key)` answers for it; the planner seats it under every hard rule. A prop
 *                 has NO function (`FURNITURE_FUNCTION` knows only catalog keys), so no storage key is minted for one.
 *   · `fixture` — a SHELL fixture seated from the descriptor, never by the planner (it touches no floor a body walks):
 *                 a unit's door frame and number plate at its `threshold` anchor, the lift's door and call panel at
 *                 `liftCandidate`, ceiling light panels, wall sconces, the sign's bracket. `shellFixtures` lists where.
 *
 * GEOMETRY. `boxes` are piece-local metres at yaw 0: x to the piece's right, y UP from the floor the piece stands on
 * (a box's CENTRE), z to the piece's FRONT (+Z — the slate's convention: a counter front faces the customer, a screen
 * has no back). `yaw` turns a box about the vertical axis, `roll` about the piece's front axis (a vault boss). Every
 * box of a `piece` lies inside w × d × h — the harness measures it; a prop draws at EXACTLY its size (no fit scale).
 *
 * MATERIAL ROLES name slice D's `shellMaterials(look)`: the five shell materials, and the lazy `partition` / `lift`
 * groups a styled look answers (`PROP_ROLE`). Under the lever `atlas:interior-look = 'off'` the two groups are absent
 * and a role falls back to its `fallback` shell material.
 *
 * COLLISION (for the visit): a `blocksWalk` piece takes its drawn box as a solid like any furniture; a `walkOn` piece
 * (a riser ≤ 0.4 m — the KCC's step) is a FLOOR the body walks on: give it a box whose top is the prop's top and whose
 * body runs ≥ 0.8 m down through the slab (law 7: thick, or not at all); a floor marking (`h` ≤ 0.05) takes none.
 *
 * `PROPS_VERSION` belongs to the kit catalog: changing an existing prop's size or `blocksWalk` moves pieces — bump
 * `KIT_VERSION` (interior_kits.ts) with it, exactly as for a catalog dimension.
 */

export const PROPS_VERSION = 'props-1';

/** What a box wears. */
export type PropRole =
  | 'wall' | 'floor' | 'stair' | 'trim' | 'glass'
  | 'panel' | 'door' | 'frame'
  | 'lift-frame' | 'cab' | 'lift-door' | 'amber'
  | 'ring'; // the call plate's ring: the builder's identity paint (slice D's `lift.ring` — a player civilisation's banner accent)

/** Where a role's material comes from in slice D's `shellMaterials(look)`, and what stands in under the lever. */
export const PROP_ROLE: Readonly<Record<PropRole, { group: 'shell' | 'partition' | 'lift'; key: string; fallback: 'wall' | 'floor' | 'stair' | 'trim' | 'glass' }>> = {
  wall: { group: 'shell', key: 'wall', fallback: 'wall' },
  floor: { group: 'shell', key: 'floor', fallback: 'floor' },
  stair: { group: 'shell', key: 'stair', fallback: 'stair' },
  trim: { group: 'shell', key: 'trim', fallback: 'trim' },
  glass: { group: 'shell', key: 'glass', fallback: 'glass' },
  panel: { group: 'partition', key: 'panel', fallback: 'wall' },       // the pale partition panel
  door: { group: 'partition', key: 'door', fallback: 'stair' },        // the tradition's wood over a kick plate
  frame: { group: 'partition', key: 'frame', fallback: 'trim' },       // the frame: honey wood (Aurelian) or gunmetal
  'lift-frame': { group: 'lift', key: 'frame', fallback: 'trim' },
  cab: { group: 'lift', key: 'cab', fallback: 'wall' },                // warm cream: cloth, a pad, a blind
  'lift-door': { group: 'lift', key: 'door', fallback: 'wall' },       // seamed alloy leaves
  amber: { group: 'lift', key: 'call', fallback: 'trim' },             // the one lit accent (the emissive grammar: people, warmth)
  ring: { group: 'lift', key: 'ring', fallback: 'trim' },              // the ring round the call light: identity, never the amber
};

export interface PropBox {
  x: number; y: number; z: number;
  w: number; h: number; d: number;
  yaw?: number; roll?: number;
  role: PropRole;
}

export interface KitPropSpec {
  key: string;
  name: string;
  kind: 'piece' | 'fixture';
  /** DRAWN size in metres (w across the front, d front to back, h tall). */
  w: number; d: number; h: number;
  /** Does a body walk AROUND it. */
  blocksWalk: boolean;
  /** A low platform a body walks ON (≤ 0.4 m): free ground to the route, a floor to the KCC. */
  walkOn?: boolean;
  /** A sight line passes it whatever its height (a rail, a glass pane). */
  seeThrough?: boolean;
  /** Its back belongs on a wall. */
  wall?: boolean;
  /** Its top carries things (a riser carries a lectern). */
  surface?: boolean;
  /** The generated model that would replace it (wanted-items.md), when one would read better. */
  wants?: string;
  desc: string;
  boxes: PropBox[];
}

// ─── Small builders ──────────────────────────────────────────────────────────────────────────────────────────────────

const bx = (role: PropRole, x: number, y: number, z: number, w: number, h: number, d: number, o: { yaw?: number; roll?: number } = {}): PropBox => ({ role, x, y, z, w, h, d, ...o });

/** One leaf of a folding screen: a cloth panel in a thin frame, turned `yaw` about its own centre. */
function screenLeaf(cx: number, yaw: number, lw: number, h: number): PropBox[] {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const at = (lx: number, y: number, w: number, hh: number, d: number, role: PropRole): PropBox => bx(role, cx + lx * c, y, -lx * s, w, hh, d, { yaw });
  return [
    at(0, h * 0.5 + 0.02, lw - 0.07, h - 0.2, 0.024, 'panel'),
    at(0, h - 0.035, lw, 0.05, 0.04, 'frame'),
    at(0, 0.1, lw, 0.05, 0.04, 'frame'),
    at(-(lw / 2 - 0.017), h / 2, 0.034, h, 0.04, 'frame'),
    at(lw / 2 - 0.017, h / 2, 0.034, h, 0.04, 'frame'),
  ];
}

const SCREEN_H = 1.8;
const LIST: KitPropSpec[] = [
  // ── PLANNER PIECES ──────────────────────────────────────────────────────────────────────────────────────────────
  { key: 'screen_panel', name: 'Folding Screen', kind: 'piece', w: 1.8, d: 0.35, h: SCREEN_H, blocksWalk: true, wants: 'privacy_screen',
    desc: 'Three cloth leaves in a shallow zigzag — it divides a room without sealing it.',
    boxes: [...screenLeaf(-0.59, 0.32, 0.6, SCREEN_H), ...screenLeaf(0, -0.32, 0.6, SCREEN_H), ...screenLeaf(0.59, 0.32, 0.6, SCREEN_H)] },
  { key: 'stage_riser', name: 'Riser', kind: 'piece', w: 3.0, d: 2.1, h: 0.3, blocksWalk: false, walkOn: true, surface: true,
    desc: 'A low dais a performer, a speaker or an officiant stands on.',
    boxes: [
      bx('stair', 0, 0.13, 0, 3.0, 0.26, 2.1),
      bx('floor', 0, 0.28, 0, 2.9, 0.04, 2.0),
      bx('trim', 0, 0.275, 1.02, 3.0, 0.05, 0.06),
      bx('amber', 0, 0.11, 1.04, 2.7, 0.03, 0.02),
    ] },
  { key: 'barrier_rail', name: 'Rail Barrier', kind: 'piece', w: 1.8, d: 0.25, h: 1.0, blocksWalk: true, seeThrough: true,
    desc: 'Two posts and a rail: a queue line, an exhibit keep-back, a machine guard.',
    boxes: [
      bx('frame', -0.77, 0.02, 0, 0.25, 0.04, 0.25), bx('frame', 0.77, 0.02, 0, 0.25, 0.04, 0.25),
      bx('frame', -0.77, 0.5, 0, 0.07, 0.96, 0.07), bx('frame', 0.77, 0.5, 0, 0.07, 0.96, 0.07),
      bx('trim', -0.77, 0.985, 0, 0.11, 0.03, 0.11), bx('trim', 0.77, 0.985, 0, 0.11, 0.03, 0.11),
      bx('door', 0, 0.88, 0, 1.5, 0.06, 0.04), bx('frame', 0, 0.46, 0, 1.5, 0.03, 0.025),
    ] },
  { key: 'counter_front', name: 'Counter Front', kind: 'piece', w: 2.2, d: 0.08, h: 1.5, blocksWalk: true, wants: 'counter_segment',
    desc: "A panelled front with a foot rail on a counter's customer side.",
    boxes: [
      bx('panel', 0, 0.76, -0.01, 2.2, 1.32, 0.05),
      bx('frame', 0, 0.05, 0, 2.2, 0.1, 0.07),
      bx('frame', 0, 1.47, 0, 2.2, 0.06, 0.08),
      bx('trim', 0, 0.24, 0.025, 2.1, 0.035, 0.03),
      bx('frame', -0.55, 0.76, 0.018, 0.035, 1.3, 0.012), bx('frame', 0.55, 0.76, 0.018, 0.035, 1.3, 0.012),
    ] },
  { key: 'teller_screen', name: 'Teller Screen', kind: 'piece', w: 2.1, d: 0.08, h: 0.7, blocksWalk: false, seeThrough: true,
    desc: 'A glass pane in a brass frame standing on a counter.',
    boxes: [
      bx('glass', 0, 0.35, 0, 2.0, 0.6, 0.016),
      bx('trim', 0, 0.02, 0, 2.1, 0.04, 0.08), bx('trim', 0, 0.68, 0, 2.1, 0.04, 0.06),
      bx('trim', -1.03, 0.35, 0, 0.04, 0.7, 0.06), bx('trim', 1.03, 0.35, 0, 0.04, 0.7, 0.06), bx('trim', 0, 0.35, 0, 0.03, 0.62, 0.04),
    ] },
  // 1.1 m DEEP on purpose: the legacy centre rule keeps every piece's centre 0.55 m off a wall, so a thin slab would
  // stand 0.3 m clear of the wall it is meant to be IN. A vault's portal is a deep reveal anyway.
  { key: 'vault_front', name: 'Vault Portal', kind: 'piece', w: 2.4, d: 1.1, h: 2.6, blocksWalk: true, wall: true, wants: 'vault_door',
    desc: 'An armoured portal: a deep gunmetal reveal, a slab door with a round boss and a spoke wheel. It never opens.',
    boxes: [
      bx('lift-frame', 0, 2.48, 0, 2.4, 0.24, 1.1), bx('lift-frame', -1.08, 1.3, 0, 0.24, 2.6, 1.1), bx('lift-frame', 1.08, 1.3, 0, 0.24, 2.6, 1.1),
      bx('lift-frame', 0, 0.06, 0, 2.4, 0.12, 1.1),
      bx('lift-door', 0, 1.24, 0.1, 1.94, 2.26, 0.3),
      bx('frame', 0, 1.3, 0.3, 1.36, 1.36, 0.12), bx('frame', 0, 1.3, 0.3, 1.36, 1.36, 0.12, { roll: Math.PI / 4 }),
      bx('trim', 0, 1.3, 0.39, 1.15, 0.07, 0.05), bx('trim', 0, 1.3, 0.39, 0.07, 1.15, 0.05),
      bx('trim', 0, 1.3, 0.39, 1.15, 0.07, 0.05, { roll: Math.PI / 4 }), bx('trim', 0, 1.3, 0.39, 0.07, 1.15, 0.05, { roll: Math.PI / 4 }),
      bx('amber', 0, 1.3, 0.43, 0.2, 0.2, 0.03),
      bx('amber', 0, 2.33, 0.545, 1.6, 0.03, 0.01),
    ] },
  { key: 'bay_marking', name: 'Bay Marking', kind: 'piece', w: 3.2, d: 5.6, h: 0.02, blocksWalk: false, walkOn: true,
    desc: 'A painted bay: the footprint a ride needs, kept clear.',
    boxes: [
      bx('stair', 0, 0.008, 0, 3.2, 0.012, 5.6),
      bx('trim', 0, 0.016, 2.74, 3.2, 0.008, 0.12), bx('trim', 0, 0.016, -2.74, 3.2, 0.008, 0.12),
      bx('trim', -1.54, 0.016, 0, 0.12, 0.008, 5.6), bx('trim', 1.54, 0.016, 0, 0.12, 0.008, 5.6),
      bx('trim', 0, 0.016, 0, 0.08, 0.008, 3.6), bx('trim', 0, 0.016, 1.9, 1.6, 0.008, 0.08),
    ] },
  { key: 'service_mat', name: 'Service Mat', kind: 'piece', w: 1.2, d: 0.8, h: 0.02, blocksWalk: false, walkOn: true,
    desc: 'Where staff stand behind a counter the shell already built.',
    boxes: [bx('stair', 0, 0.008, 0, 1.2, 0.012, 0.8), bx('trim', 0, 0.016, 0.37, 1.2, 0.008, 0.06), bx('trim', 0, 0.016, -0.37, 1.2, 0.008, 0.06)] },
  { key: 'curtain_drape', name: 'Stage Drape', kind: 'piece', w: 1.2, d: 0.3, h: 2.7, blocksWalk: true, wall: true, wants: 'stage_curtain',
    desc: 'A full-height cloth drape under a valance.',
    boxes: [
      ...[-0.46, -0.23, 0, 0.23, 0.46].map((x, i) => bx('cab', x, 1.25, i % 2 === 0 ? -0.04 : 0.05, 0.26, 2.5, 0.09)),
      bx('frame', 0, 2.58, 0, 1.2, 0.24, 0.3), bx('trim', 0, 2.45, 0.14, 1.2, 0.03, 0.02),
    ] },
  { key: 'kneeler_pad', name: 'Kneeler', kind: 'piece', w: 1.2, d: 0.5, h: 0.12, blocksWalk: false, walkOn: true,
    desc: 'A low padded kneeler before a shrine focus.',
    boxes: [bx('frame', 0, 0.03, 0, 1.2, 0.06, 0.5), bx('cab', 0, 0.09, 0, 1.1, 0.06, 0.4)] },

  // ── SHELL FIXTURES (seated from the descriptor — `shellFixtures`) ────────────────────────────────────────────────
  { key: 'door_plate', name: 'Door Plate', kind: 'fixture', w: 0.3, d: 0.03, h: 0.14, blocksWalk: false,
    desc: "A unit's number plate; the number is dynamic text (signTexture kind 'plate') on its +Z face.",
    boxes: [bx('frame', 0, 0.07, -0.005, 0.3, 0.14, 0.02), bx('panel', 0, 0.07, 0.008, 0.26, 0.1, 0.008)] },
  { key: 'unit_door', name: 'Unit Door', kind: 'fixture', w: 1.0, d: 0.12, h: 2.1, blocksWalk: false, wants: 'unit_door',
    desc: 'A door frame with its leaf standing open against the jamb — walked through until rooms are enclosed (slice G).',
    boxes: [
      bx('frame', -0.47, 1.05, 0, 0.06, 2.1, 0.12), bx('frame', 0.47, 1.05, 0, 0.06, 2.1, 0.12), bx('frame', 0, 2.06, 0, 1.0, 0.08, 0.12),
      bx('door', -0.44, 1.01, 0.47, 0.86, 2.0, 0.04, { yaw: Math.PI / 2 }),
    ] },
  { key: 'lift_door', name: 'Lift Door', kind: 'fixture', w: 1.6, d: 0.14, h: 2.2, blocksWalk: false, wants: 'lift_door',
    desc: 'A two-leaf shaft door in its frame (closed; the lift is slice L).',
    boxes: [
      bx('lift-frame', -0.74, 1.1, 0, 0.12, 2.2, 0.14), bx('lift-frame', 0.74, 1.1, 0, 0.12, 2.2, 0.14), bx('lift-frame', 0, 2.13, 0, 1.6, 0.14, 0.14),
      bx('lift-door', -0.34, 1.03, -0.02, 0.67, 2.06, 0.04), bx('lift-door', 0.34, 1.03, -0.02, 0.67, 2.06, 0.04),
      bx('amber', 0, 2.13, 0.064, 0.5, 0.04, 0.01),
    ] },
  { key: 'lift_call_panel', name: 'Lift Call Panel', kind: 'fixture', w: 0.18, d: 0.06, h: 0.32, blocksWalk: false,
    desc: 'A call plate with an amber ring.',
    // (C2 item 7) the RING: a 2 cm band of the builder's paint round the amber call light, proud of the plate — the
    // light stays amber (it shares `lift.call` with every ceiling light and sconce; the accent could not ride it)
    boxes: [bx('lift-frame', 0, 0.16, 0, 0.18, 0.32, 0.04), bx('ring', 0, 0.2, 0.021, 0.12, 0.12, 0.006), bx('amber', 0, 0.2, 0.02, 0.08, 0.08, 0.02)] },
  { key: 'ceiling_light_panel', name: 'Ceiling Light Panel', kind: 'fixture', w: 1.2, d: 0.3, h: 0.08, blocksWalk: false,
    desc: 'A flush ceiling fixture; its lit face looks down. Hangs with its TOP at the slab.',
    boxes: [bx('frame', 0, 0.05, 0, 1.2, 0.06, 0.3), bx('amber', 0, 0.012, 0, 1.1, 0.024, 0.22)] },
  { key: 'sign_bracket', name: 'Sign Bracket', kind: 'fixture', w: 1.5, d: 0.12, h: 0.55, blocksWalk: false,
    desc: 'Two rods and a rail that carry the sign plane (hangs with its TOP at the header).',
    boxes: [bx('frame', 0, 0.53, 0, 1.5, 0.04, 0.05), bx('frame', -0.7, 0.26, 0, 0.02, 0.52, 0.02), bx('frame', 0.7, 0.26, 0, 0.02, 0.52, 0.02), bx('frame', 0, 0.02, 0, 1.5, 0.03, 0.03)] },
  { key: 'wall_sconce', name: 'Wall Sconce', kind: 'fixture', w: 0.22, d: 0.14, h: 0.38, blocksWalk: false, wants: 'wall_sconce',
    desc: 'A back-plate and a lit shade; its back is its −Z face.',
    boxes: [bx('frame', 0, 0.19, -0.055, 0.22, 0.38, 0.03), bx('amber', 0, 0.21, 0.02, 0.13, 0.2, 0.1), bx('trim', 0, 0.09, 0.0, 0.16, 0.025, 0.1)] },
  { key: 'window_shade', name: 'Window Shade', kind: 'fixture', w: 2.4, d: 0.08, h: 1.7, blocksWalk: false,
    desc: 'A half-drawn roller blind in its cassette (hangs with its TOP at the window head).',
    boxes: [bx('frame', 0, 1.65, 0, 2.4, 0.1, 0.08), bx('cab', 0, 1.2, 0, 2.3, 0.8, 0.01), bx('frame', 0, 0.8, 0, 2.3, 0.03, 0.025)] },
];

export const KIT_PROPS: Readonly<Record<string, KitPropSpec>> = Object.fromEntries(LIST.map((p) => [p.key, p]));
export const KIT_PROP_KEYS: readonly string[] = LIST.map((p) => p.key);
export function kitProp(key: string): KitPropSpec | null { return Object.prototype.hasOwnProperty.call(KIT_PROPS, key) ? KIT_PROPS[key] : null; }
/** Is this piece key a procedural prop (not a catalog model). */
export function isKitProp(key: string): boolean { return Object.prototype.hasOwnProperty.call(KIT_PROPS, key); }

/** A prop's axis-aligned drawn half extents on the room axes at `yaw` — `drawnHalf`'s shape for a prop. */
export function propHalf(key: string, yaw: number): { hx: number; hz: number } {
  const p = kitProp(key);
  if (!p) return { hx: 0.5, hz: 0.5 };
  const c = Math.abs(Math.cos(yaw)), n = Math.abs(Math.sin(yaw));
  return { hx: c * (p.w / 2) + n * (p.d / 2), hz: n * (p.w / 2) + c * (p.d / 2) };
}

/** The local-space bounds its boxes really fill (the harness holds them inside w × d × h for a `piece`). */
export function propBounds(p: KitPropSpec): { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number } {
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const b of p.boxes) {
    // the box's corners after roll (about Z) then yaw (about Y)
    const cy = Math.cos(b.yaw ?? 0), sy = Math.sin(b.yaw ?? 0), cr = Math.cos(b.roll ?? 0), sr = Math.sin(b.roll ?? 0);
    for (const sx of [-1, 1]) for (const sv of [-1, 1]) for (const sz of [-1, 1]) {
      const lx = (sx * b.w) / 2, ly = (sv * b.h) / 2, lz = (sz * b.d) / 2;
      const rx = lx * cr - ly * sr, ry = lx * sr + ly * cr;      // roll
      const wx = rx * cy + lz * sy, wz = -rx * sy + lz * cy;      // yaw (three's rotation.y)
      x0 = Math.min(x0, b.x + wx); x1 = Math.max(x1, b.x + wx);
      y0 = Math.min(y0, b.y + ry); y1 = Math.max(y1, b.y + ry);
      z0 = Math.min(z0, b.z + wz); z1 = Math.max(z1, b.z + wz);
    }
  }
  return { x0, x1, y0, y1, z0, z1 };
}

// ─── Shell fixtures: where the descriptor says a fixture goes ────────────────────────────────────────────────────────

export interface FixturePlacement {
  key: string; floor: number;
  /** Building-local metres; `y` is the fixture's BASE above the floor's slab (`DescFloor.floorY`). */
  x: number; y: number; z: number;
  /** The plans' yaw: `rotation.y = buildingYaw + yaw`; 0 faces +Z. */
  yaw: number;
  /** Dynamic text for a plate (slice D's `signTexture(text, look, { kind: 'plate' })`). */
  text?: string;
  /** What it marks (a unit id, `lift`, `ceiling`, `door`). */
  of: string;
}
/** The slice of a descriptor `shellFixtures` reads — structural, so this file imports nothing. */
export interface FixtureFloor {
  floor: number;
  units: ReadonlyArray<{ unitId: string; threshold: string; rect: { x0: number; z0: number; x1: number; z1: number } }>;
  anchors: ReadonlyArray<{ id: string; kind: string; x: number; z: number; ax: number; az: number }>;
  /** (slice G) the floor's rooms and doorways — at the rooms revision a unit is a ROOM behind a wall with a framed doorway. */
  rooms?: ReadonlyArray<{ id: string; unitId?: string }>;
  doorways?: ReadonlyArray<{ a: string; b: string; x: number; z: number; width: number; ax: number; az: number; bx: number; bz: number }>;
}
export interface FixtureInput {
  floors: ReadonlyArray<FixtureFloor>;
  liftCandidate: { rect: { x0: number; z0: number; x1: number; z1: number }; door: 'east' | 'south' | 'west'; calls: ReadonlyArray<{ floor: number }> } | null;
}
export interface FixtureShell { hw: number; hd: number; floors: number; stair: { x: number; z: number; w: number; d: number } }
export interface FixtureOptions {
  /** Ceiling light panels on a grid (clinics, workshops, civic halls, banks, concourses). */
  ceilingLights?: boolean;
  /** The grid's pitch (m). */
  ceilingPitch?: number;
  /** The slab-to-slab height (m) — `FLOOR_H`. */
  floorH?: number;
  /** A sconce either side of the door inside. */
  doorSconces?: boolean;
}
/** A unit's number as a plate reads it: floor 2, unit 0 → "201". */
export function unitNumber(floor: number, index: number): string { return `${floor}${String(index + 1).padStart(2, '0')}`; }

/**
 * The fixtures a planned interior implies, PURE: per unit a door frame on its threshold with its number plate beside
 * it, per floor the lift's shaft door and call panel, and (asked for) ceiling light panels on a grid clear of the
 * stairwell and a sconce either side of the door. Nothing here collides; nothing here is in the digest.
 */
/** Half a partition's thickness (m) — the planner's `PARTITION_MM` / 2 (this file imports nothing; the harness pins it). */
export const UNIT_WALL_HALF_M = 0.2;
export function shellFixtures(shell: FixtureShell, d: FixtureInput, opts: FixtureOptions = {}): FixturePlacement[] {
  const out: FixturePlacement[] = [];
  const HP = Math.PI / 2;
  for (const fl of d.floors) {
    for (const u of fl.units) {
      const t = fl.anchors.find((a) => a.id === u.threshold);
      if (!t) continue;
      const index = Number(/\/(\d+)$/.exec(u.unitId)?.[1] ?? 0);
      // (slice G) A UNIT THAT IS A ROOM: its wall carries the doorway's frame (the partition's own jambs and lintel), so
      // no second frame and no leaf standing open into the corridor — only the number plate, on the corridor face of
      // the wall beside the doorway, facing whoever walks up to it.
      const room = fl.rooms?.find((r) => r.unitId === u.unitId);
      const way = room ? fl.doorways?.find((q) => q.a === room.id || q.b === room.id) : undefined;
      if (room && way) {
        const ox = way.a === room.id ? way.bx : way.ax, oz = way.a === room.id ? way.bz : way.az; // the side that is not the unit
        const len = Math.hypot(ox - way.x, oz - way.z) || 1;
        const nx = (ox - way.x) / len, nz = (oz - way.z) / len;
        const face = UNIT_WALL_HALF_M + 0.016, along = way.width / 2 + 0.34;
        out.push({ key: 'door_plate', floor: fl.floor, x: way.x + nx * face + nz * along, y: 1.55, z: way.z + nz * face - nx * along, yaw: Math.atan2(nx, nz), text: unitNumber(fl.floor, index), of: u.unitId });
        continue;
      }
      // the frame stands ON the unit's corridor edge, facing the corridor (a unit's rect lies east of its threshold)
      const onWestEdge = Math.abs(t.x - u.rect.x0) < 0.02;
      if (onWestEdge) {
        out.push({ key: 'unit_door', floor: fl.floor, x: u.rect.x0, y: 0, z: t.z, yaw: -HP, of: u.unitId });
        out.push({ key: 'door_plate', floor: fl.floor, x: u.rect.x0 - 0.07, y: 1.55, z: t.z + 0.72, yaw: -HP, text: unitNumber(fl.floor, index), of: u.unitId });
      } else {
        // a spare room has no wall yet: a plate on a post-less mount at eye height by the threshold
        out.push({ key: 'door_plate', floor: fl.floor, x: t.ax, y: 1.55, z: t.az, yaw: 0, text: unitNumber(fl.floor, index), of: u.unitId });
      }
    }
    if (d.liftCandidate && d.liftCandidate.calls.some((c) => c.floor === fl.floor)) {
      const r = d.liftCandidate.rect;
      const cx = (r.x0 + r.x1) / 2, cz = (r.z0 + r.z1) / 2;
      if (d.liftCandidate.door === 'east') {
        out.push({ key: 'lift_door', floor: fl.floor, x: r.x1, y: 0, z: cz, yaw: HP, of: 'lift' });
        out.push({ key: 'lift_call_panel', floor: fl.floor, x: r.x1 + 0.03, y: 1.05, z: r.z0 - 0.2, yaw: HP, of: 'lift' });
      } else if (d.liftCandidate.door === 'west') {
        out.push({ key: 'lift_door', floor: fl.floor, x: r.x0, y: 0, z: cz, yaw: -HP, of: 'lift' });
        out.push({ key: 'lift_call_panel', floor: fl.floor, x: r.x0 - 0.03, y: 1.05, z: r.z0 - 0.2, yaw: -HP, of: 'lift' });
      } else {
        out.push({ key: 'lift_door', floor: fl.floor, x: cx, y: 0, z: r.z0, yaw: Math.PI, of: 'lift' });
        out.push({ key: 'lift_call_panel', floor: fl.floor, x: r.x1 + 0.2, y: 1.05, z: r.z0 - 0.03, yaw: Math.PI, of: 'lift' });
      }
    }
    if (opts.ceilingLights) {
      const pitch = Math.max(2.5, opts.ceilingPitch ?? 5);
      const top = (opts.floorH ?? 3.4) - (fl.floor < shell.floors - 1 ? 0.5 : 0.0) - 0.02;
      const nx = Math.max(1, Math.floor((2 * shell.hw - 1.2) / pitch)), nz = Math.max(1, Math.floor((2 * shell.hd - 1.2) / pitch));
      const st = shell.stair;
      for (let i = 0; i < nx; i++) for (let j = 0; j < nz; j++) {
        const x = -((nx - 1) * pitch) / 2 + i * pitch, z = -((nz - 1) * pitch) / 2 + j * pitch;
        // never over the stairwell (the slab is open there on every floor that has a run or an opening)
        if (shell.floors > 1 && x < st.x + st.w / 2 + 1.0 && z > st.z - st.d / 2 - 1.2 && z < st.z + st.d / 2 + 1.2) continue;
        out.push({ key: 'ceiling_light_panel', floor: fl.floor, x, y: top - 0.08, z, yaw: 0, of: 'ceiling' });
      }
    }
    if (opts.doorSconces && fl.floor === 0 && shell.hw > 2.2) {
      for (const sx of [-1, 1]) out.push({ key: 'wall_sconce', floor: 0, x: sx * Math.min(1.75, shell.hw - 0.4), y: 1.75, z: -shell.hd + 0.07, yaw: 0, of: 'door' });
    }
  }
  return out;
}
